<script lang="ts">
  import type { Segment } from '$lib/stores/aiFeatures'

  /**
   * CompletionController: Handles completion message display logic
   * Single Responsibility: Manage completion state and messages
   */

  interface Props {
    aiSegments: Segment[]
    onSegmentHover?: (segmentId: string | null) => void
  }

  let { 
    aiSegments = [],
    onSegmentHover
  }: Props = $props()

  // Completion state
  let showCompletionMessage = $state(false)
  let userDismissedCompletion = $state(false)
  let showApplyMessage = $state(false)

  // Calculate counts
  let appliedCount = $derived(
    aiSegments.filter(seg => seg.status === 'applied').length
  )
  
  let skippedCount = $derived(
    aiSegments.filter(seg => seg.status === 'skipped').length
  )

  // Check if all segments are processed
  let allProcessed = $derived(
    aiSegments.length > 0 && 
    aiSegments.every(seg => seg.status === 'applied' || seg.status === 'skipped')
  )

  // Update completion message visibility when segments change
  $effect(() => {
    if (allProcessed && !userDismissedCompletion) {
      showCompletionMessage = true
      // Clear any hover states when showing completion
      onSegmentHover?.(null)
    } else if (!allProcessed) {
      // Reset flags when not all processed
      showCompletionMessage = false
      userDismissedCompletion = false
      showApplyMessage = false
    }
    
    // Show apply message when returned to editing mode with all processed
    if (allProcessed && userDismissedCompletion && !showCompletionMessage) {
      showApplyMessage = true
    }
  })

  // Export state getters
  export function isShowingCompletionMessage() {
    return showCompletionMessage
  }

  export function isShowingApplyMessage() {
    return showApplyMessage
  }

  export function getAppliedCount() {
    return appliedCount
  }

  export function getSkippedCount() {
    return skippedCount
  }

  // Actions
  export function dismissCompletion() {
    showCompletionMessage = false
    userDismissedCompletion = true
    showApplyMessage = true
    onSegmentHover?.(null)
  }

  export function hideApplyMessage() {
    showApplyMessage = false
  }

  export function resetCompletionState() {
    showCompletionMessage = false
    userDismissedCompletion = false
    showApplyMessage = false
  }
</script>